import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ChatService } from 'src/app/service/chat.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HighlightTextPipe } from 'src/app/pipe/highlight-text.pipe';

@Component({
  selector: 'app-advanced-search',
  standalone: true,
  imports: [CommonModule, FormsModule, InfiniteScrollModule, HighlightTextPipe],
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.css']
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {
  @Input() userId: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() searchResults = new EventEmitter<{ results: any[], filters: any, matchesCount?: number }>();
  @Input() isMobileView: boolean = false;
  @Input() showResultsOnMobile: boolean = false;
  @Output() selectChat = new EventEmitter<any>();
  @Output() openPreview = new EventEmitter<any>();
  @Output() backToFilters = new EventEmitter<void>();
  @Output() loading = new EventEmitter<boolean>();

  searchQuery: string = '';
  searchIn: 'users' | 'groups' | '' = '';
  selectedType: string = '';
  selectedSubtype: string = '';
  direction: 'all' | 'incoming' | 'outgoing' | '' = '';
  startDate: string = '';
  endDate: string = '';

  subtypesMap: { [key: string]: string[] } = {
    'photo': ['jpg', 'png', 'url', 'text'],
    'video': ['mp4', 'mov', 'url', 'text'],
    'doc': ['pdf', 'doc', 'excel', 'ppt', 'url', 'text'],
    'link': ['image', 'video', 'doc', 'excel', 'ppt', 'mp3'],
    'audio': ['url', 'text', 'mp3']
  };

  // Maps internal API value → display label shown in the UI
  subtypeDisplayMap: { [key: string]: string } = {
    'image': 'Photo',
    'mp3': 'MP3',
    'excel': 'sheets',
    'pdf': 'PDF',
    'doc': 'Doc',
    'ppt': 'PPT',
    'url': 'URL',
    'text': 'Text',
    'jpg': 'JPG',
    'png': 'PNG',
    'mp4': 'MP4',
    'mov': 'MOV'
  };

  // Mentions logic
  allChats: any[] = [];
  filteredMentions: any[] = [];
  showMentions: boolean = false;
  selectedChats: any[] = [];

  savedDrafts: any[] = [];
  results: any[] = [];
  isSearching: boolean = false;
  pageNum: number = 1;
  limit: number = 100;
  hasMore: boolean = true;

  draftPageNum: number = 1;
  draftLimit: number = 10;
  hasMoreDrafts: boolean = true;
  isLoadingDrafts: boolean = false;

  private searchInput$ = new Subject<string>();
  private searchSub: Subscription;

  constructor(
    public chatService: ChatService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadDrafts();

    // Fetch chats for autocomplete
    this.chatService.getChats().subscribe({
      next: (res: any) => {
        const chats = res?.data || res || [];
        this.allChats = chats.map((c: any) => ({
          id: c.channelId || c._id || c.id,
          name: c.groupName || c.displayName || c.name || (c.firstName ? c.firstName + ' ' + (c.lastName || '') : '') || c.email || 'Unknown',
          type: c.type || (c.groupName ? 'group' : 'single'),
          image: c.profileImg || c.profileImage || c.groupImg || c.groupImage || c.image || ''
        }));
      }
    });

    // Debounce search input — fires API 400ms after user stops typing
    this.searchSub = this.searchInput$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        if (!this.isMobileView) {
          this.onSearch();
        }
      });
  }

  getFilters(): any {
    const field: any = {};

    if (this.searchQuery?.trim()) field.search = this.searchQuery.trim();

    if (this.searchIn) {
      field.chatType = this.searchIn === 'users' ? 'single' : 'group';
    }

    if (this.selectedType) {
      const typeMap: { [key: string]: string } = {
        'photo': 'image',
        'video': 'video',
        'doc': 'doc',
        'docs': 'doc',
        'audio': 'audio',
        'link': 'link',
        'other': 'text'
      };
      field.contentType = typeMap[this.selectedType] || this.selectedType;
    }

    if (this.selectedSubtype) {
      if (['url', 'text'].includes(this.selectedSubtype)) {
        field.captionContentType = this.selectedSubtype;
      } else {
        const subtypeValueMap: { [key: string]: string } = {
          'image': 'image',
          'photo': 'image',
        };
        field.contentSubtype = subtypeValueMap[this.selectedSubtype] ?? this.selectedSubtype;
      }
    }

    if (this.direction && this.direction !== 'all') {
      field.direction = this.direction;
    }

    if (this.startDate) {
      try {
        field.startDate = new Date(this.startDate).toISOString();
      } catch (e) {
        console.error("Invalid start date", e);
      }
    }

    if (this.endDate) {
      try {
        field.endDate = new Date(this.endDate).toISOString();
      } catch (e) {
        console.error("Invalid end date", e);
      }
    }

    if (this.selectedChats.length > 0) {
      field.chatIds = this.selectedChats.map(c => c.id);
    }

    return field;
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  onSearchQueryChange(value: string) {
    const atIndex = value.lastIndexOf('@');
    if (atIndex !== -1 && (atIndex === 0 || !/[\w.+-]/.test(value[atIndex - 1]))) {
      const query = value.substring(atIndex + 1).toLowerCase();
      this.filteredMentions = this.allChats.filter(c => {
        const matchesQuery = c.name.toLowerCase().includes(query);
        let matchesType = true;
        if (this.searchIn === 'groups') {
          matchesType = c.type === 'group';
        } else if (this.searchIn === 'users') {
          matchesType = c.type === 'single';
        }
        return matchesQuery && matchesType;
      });
      this.showMentions = true;
    } else {
      this.showMentions = false;
      this.filteredMentions = [];
    }

    this.searchInput$.next(value);
  }
 
  clearSearch() {
    this.searchQuery = '';
    this.onSearchQueryChange('');
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  selectMention(mention: any) {
    if (!this.selectedChats.find(c => c.id === mention.id)) {
      this.selectedChats.push(mention);
    }
    this.showMentions = false;

    // Remove the @... text
    const atIndex = this.searchQuery.lastIndexOf('@');
    if (atIndex !== -1) {
      this.searchQuery = this.searchQuery.substring(0, atIndex).trim();
    }

    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  closeMentions() {
    this.showMentions = false;
    const atIndex = this.searchQuery.lastIndexOf('@');
    if (atIndex !== -1) {
      this.searchQuery = this.searchQuery.substring(0, atIndex).trim();
    }
  }

  removeChatTag(chat: any) {
    this.selectedChats = this.selectedChats.filter(c => c.id !== chat.id);
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onSearchInSelect(value: 'users' | 'groups') {
    this.searchIn = this.searchIn === value ? '' : value;
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onTypeSelect(type: string) {
    this.selectedType = this.selectedType === type ? '' : type;
    this.selectedSubtype = ''; // reset subtype
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onSubtypeSelect(subtype: string) {
    this.selectedSubtype = this.selectedSubtype === subtype ? '' : subtype;
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onDirectionSelect(dir: 'all' | 'incoming' | 'outgoing') {
    this.direction = this.direction === dir ? '' : dir;
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onDateChange() {
    if (!this.isMobileView) {
      this.onSearch();
    }
  }

  onSearch(isLoadMore: boolean = false) {
    if (!isLoadMore) {
      this.pageNum = 1;
      this.results = [];
      this.hasMore = true;
    }
    this.isSearching = true;

    const field = this.getFilters();
    field.pageNum = this.pageNum;
    field.limit = this.limit;
    field.searchText = field.search; // searchMessages might still expect searchText or handle both

    console.log('Performing advanced search with field:', field);
    this.performSearch(field);
  }

  searchNextPage() {
    if (this.isSearching || !this.hasMore) return;
    this.pageNum++;
    this.onSearch(true);
  }

  performSearch(filters: any) {
    this.loading.emit(true);
    this.chatService.searchChatMessages(filters).subscribe({
      next: (res: any) => {
        this.isSearching = false;
        this.loading.emit(false);
        const messages = res?.data?.results || [];
        const totalCount = res?.data?.totalCount || 0;

        if (messages.length < this.limit) {
          this.hasMore = false;
        }

        const enrichedResults = messages.map((msg: any) => {
          const chatType = msg.chatType || (this.searchIn === 'users' ? 'single' : 'group');
          return {
            ...msg,
            fromSearch: true,
            chatType: chatType,
            userId: msg.userId || (chatType === 'single' ? msg.sender_id : ''),
            channelId: msg.channelId || msg.channel_id || msg.meta?.channelId || msg.meta?.channel_id || '',
            displayName: msg.chatName || msg.name || (chatType === 'group' ? 'Group' : 'User'),
            lastMessageTimeAgo: this.getTimeAgo(msg.createdAt),
            typeTag: msg.contentType || msg.meta?.contentType || '',
            subtypeTag: msg.contentSubtype || msg.meta?.contentSubtype || msg.meta?.subType || '',
            lastMessage: {
              _id: msg._id || msg.id || msg.messageId || msg.message_id || msg.meta?.messageId || msg.meta?.message_id,
              id: msg.id || msg._id || msg.messageId || msg.message_id || msg.meta?.messageId || msg.meta?.message_id,
              messageId: msg.messageId || msg._id || msg.id || msg.message_id || msg.meta?.messageId || msg.meta?.message_id,
              message: msg.message || '',
              createdAt: msg.createdAt
            }
          };
        });

        if (this.pageNum === 1) {
          this.results = enrichedResults;
        } else {
          this.results = [...this.results, ...enrichedResults];
        }

        this.searchResults.emit({
          results: this.results,
          matchesCount: totalCount,
          filters: {
            selectedType: this.selectedType,
            searchIn: this.searchIn,
            selectedSubtype: this.selectedSubtype,
            search: this.searchQuery
          }
        });
        console.log("Results updated in AdvancedSearchComp:", { resultsCount: this.results.length, isMobile: this.isMobileView, showResultsOnMobile: this.showResultsOnMobile });
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error("Advanced search failed:", err);
        this.isSearching = false;
        this.loading.emit(false);
        if (this.pageNum === 1) {
          this.results = [];
          this.searchResults.emit({ results: [], filters: {} });
        }
      }
    });
  }

  getTimeAgo(date: any): string {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return this.formatDateManual(then);
  }

  onReset() {
    this.searchQuery = '';
    this.searchIn = '';
    this.selectedType = '';
    this.selectedSubtype = '';
    this.direction = '';
    this.startDate = '';
    this.endDate = '';
    this.results = [];
    this.pageNum = 1;
    this.hasMore = true;
    this.isSearching = false;
    this.selectedChats = [];
    this.showMentions = false;
    this.filteredMentions = [];
    this.searchResults.emit({ results: [], filters: {}, matchesCount: 0 });
  }

  saveAsDraft() {
    const filters = this.getFilters();
    const body = {
      ...filters,
      name: `Search ${this.formatDateManual(new Date())} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    };

    this.chatService.createSearchDraft(body).subscribe({
      next: (res: any) => {
        console.log("Draft saved successfully", res);
        this.loadDrafts();
      },
      error: (err) => {
        console.error("Failed to save draft", err);
      }
    });
  }

  loadDrafts(isLoadMore: boolean = false) {
    if (!isLoadMore) {
      this.draftPageNum = 1;
      this.savedDrafts = [];
      this.hasMoreDrafts = true;
    }

    if (this.isLoadingDrafts) return;
    this.isLoadingDrafts = true;

    this.chatService.getAllDrafts(this.draftPageNum, this.draftLimit).subscribe({
      next: (res: any) => {
        this.isLoadingDrafts = false;
        const presets = res?.data?.searchPresets || [];

        if (presets.length < this.draftLimit) {
          this.hasMoreDrafts = false;
        }

        if (isLoadMore) {
          this.savedDrafts = [...this.savedDrafts, ...presets];
        } else {
          this.savedDrafts = presets;
        }
        console.log("Drafts loaded:", this.savedDrafts);
      },
      error: (err) => {
        this.isLoadingDrafts = false;
        console.error("Failed to load drafts", err);
      }
    });
  }

  loadMoreDrafts() {
    if (this.isLoadingDrafts || !this.hasMoreDrafts) return;
    this.draftPageNum++;
    this.loadDrafts(true);
  }

  private formatDateManual(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  getDraftDateRange(draft: any): string {
    if (draft.startDate && draft.endDate) {
      const start = this.formatDateManual(draft.startDate);
      const end = this.formatDateManual(draft.endDate);
      return `${start} - ${end}`;
    } else if (draft.startDate) {
      const start = this.formatDateManual(draft.startDate);
      return `From ${start}`;
    } else if (draft.endDate) {
      const end = this.formatDateManual(draft.endDate);
      return `Until ${end}`;
    }
    return "";
  }

  getDraftFilterSummary(draft: any): string {
    const parts = [];

    // 1. Chat Type
    if (draft.chatType) {
      parts.push(draft.chatType === 'group' ? 'Groups' : 'Single');
    }

    // 2. Content Type
    if (draft.contentType) {
      const typeLabelMap: { [key: string]: string } = {
        'image': 'Photo',
        'video': 'Video',
        'doc': 'Doc',
        'audio': 'Audio',
        'link': 'Link',
        'text': 'Other'
      };
      parts.push(typeLabelMap[draft.contentType] || draft.contentType);
    }

    // 3. Subtype
    const sub = draft.contentSubtype || draft.captionContentType;
    if (sub) {
      parts.push(this.subtypeDisplayMap[sub] || sub);
    }

    // 4. Direction
    if (draft.direction) {
      const dirMap: { [key: string]: string } = {
        'all': 'All',
        'incoming': 'Inbound',
        'outgoing': 'Outbound'
      };
      parts.push(dirMap[draft.direction] || draft.direction);
    }

    // 5. Query
    if (draft.search) {
      parts.push(`"${draft.search}"`);
    }

    return parts.join(' • ');
  }

  getDraftDisplayName(draft: any): string {
    const range = this.getDraftDateRange(draft);
    return range ? `${range}` : 'Draft';
  }

  deleteDraft(id: string) {
    this.chatService.deleteSearchDraft(id).subscribe({
      next: () => {
        this.loadDrafts();
      },
      error: (err) => {
        console.error("Failed to delete draft", err);
      }
    });
  }

  applyDraft(draft: any) {
    const f = draft;
    this.searchQuery = f.search || '';
    this.searchIn = f.chatType === 'group' ? 'groups' : (f.chatType === 'single' ? 'users' : '');

    // Reverse map contentType to selectedType
    const reverseTypeMap: { [key: string]: string } = {
      'image': 'photo',
      'video': 'video',
      'doc': 'doc',
      'docs': 'doc',
      'audio': 'audio',
      'link': 'link',
      'text': 'other'
    };
    this.selectedType = reverseTypeMap[f.contentType] || f.contentType || '';

    this.selectedSubtype = f.contentSubtype || f.captionContentType || '';
    this.direction = f.direction || '';

    if (f.startDate) {
      this.startDate = new Date(f.startDate).toISOString().split('T')[0];
    } else {
      this.startDate = '';
    }

    if (f.endDate) {
      this.endDate = new Date(f.endDate).toISOString().split('T')[0];
    } else {
      this.endDate = '';
    }

    // Trigger search after state is updated
    this.onSearch();
  }

  onClose() {
    this.onReset();
    this.close.emit();
  }

  onOpenPreview(chat: any) {
    console.log("Emitting openPreview event for:", chat);
    this.openPreview.emit({ chat, allMedia: this.results });
  }

  getDocColor(chat: any): string {
    const fileName = (chat.message || '').toLowerCase();
    if (fileName.endsWith('.pdf')) return '#f44336';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return '#2196f3';
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return '#4caf50';
    if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx')) return '#ff9800';
    return '#9e9e9e';
  }

  getDocLabel(chat: any): string {
    const fileName = (chat.message || '').toLowerCase();
    if (fileName.endsWith('.pdf')) return 'PDF';
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'DOC';
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return 'XLS';
    if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx')) return 'PPT';
    if (fileName.endsWith('.csv')) return 'CSV';
    return 'FILE';
  }

  onChatClick(chat: any) {
    if (this.canPreviewMedia()) {
      this.openPreview.emit({ chat, allMedia: this.results });
    } else {
      this.selectChat.emit({ ...chat, fromSearch: true, isAdvancedSearch: true, lastMessage: chat });
    }
  }

  canPreviewMedia(): boolean {
    return this.selectedType === 'photo' || this.selectedType === 'video';
  }

  handleProfileImageError(event: any, isGroup: boolean = false) {
    event.target.src = isGroup ? 'assets/group_chat.png' : 'assets/user-icon.png';
  }

  getCleanFileName(chat: any): string {
    const src = chat.fileName || chat.meta?.ogFileName || chat.name || chat.message || '';
    if (!src || typeof src !== 'string' || src.includes(' ') || (src.length < 10 && !src.includes('.'))) {
      return chat.message || '';
    }
    const base = src.split('/').pop() || src;
    const cleaned = base.replace(/^(\d{10,}[-_])+/, '');
    return decodeURIComponent(cleaned);
  }

  isLink(chat: any): boolean {
    const type = chat.typeTag || chat.contentType || chat.meta?.contentType || '';
    const message = chat.message || chat.lastMessage?.message || '';
    return type === 'link' || /^(http|https):\/\/[^ "]+$/.test(message.trim());
  }

  getLinkDomain(url: string): string {
    if (!url) return '';
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return url;
    }
  }
}
